import { ChevronLeftIcon, ChevronRightIcon, XIcon } from '@heroicons/react/outline';
import { Carousel, Modal as AntModal } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { useRouter } from 'next/router';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { EventDetails, logEvent } from '@/hooks/useClickEventTracking';
import useTailwindBreakpoint from '@/hooks/useTailwindBreakpoint';

import { user } from '@/atoms/user';
import {
  getRoutePopup,
  IRoutePopup,
  IRoutePopupImage,
} from '@/services/RoutePopup';
import { User } from '@/utils/interfaces/User';

const POPUP_HIDE_KEY_PREFIX = 'routePopupClosed_';

const isRoutePopupHidden = (popupId: string): boolean => {
  if (typeof window === 'undefined' || !popupId) return false;
  const expiry = localStorage.getItem(`${POPUP_HIDE_KEY_PREFIX}${popupId}`);
  if (!expiry) return false;

  const expiryTime = Number(expiry);
  if (Number.isNaN(expiryTime)) {
    localStorage.removeItem(`${POPUP_HIDE_KEY_PREFIX}${popupId}`);
    return false;
  }

  if (Date.now() > expiryTime) {
    localStorage.removeItem(`${POPUP_HIDE_KEY_PREFIX}${popupId}`);
    return false;
  }

  return true;
};

const getImageKeyForScreen = (
  image: IRoutePopupImage,
  screens: ReturnType<typeof useTailwindBreakpoint>,
): string => {
  if (screens.lg) return image.webKey;
  if (screens.sm) return image.tabKey || image.webKey;
  return image.phoneKey || image.webKey;
};

interface RoutePopupModalContentProps {
  onClose: () => void;
  popupData: IRoutePopup;
}

const RoutePopupModalContent: React.FC<RoutePopupModalContentProps> = ({
  onClose,
  popupData,
}) => {
  const screens = useTailwindBreakpoint();
  const carouselRef = useRef<any>(null);
  const images = popupData.images || [];

  if (images.length === 0) return null;

  const handleImageClick = (image: IRoutePopupImage) => {
    if (image.link && typeof window !== 'undefined') {
      window.open(image.link, '_blank');
    }
    onClose();
  };

  const isMultiple = images.length > 1;

  return (
    <div className='relative w-[358px] sm:w-[600px] lg:w-[1000px] max-w-[98vw] rounded-xl overflow-hidden'>
      {/* Close button */}
      <button
        onClick={onClose}
        aria-label='Close modal'
        className='absolute top-3 right-3 text-white hover:text-white transition-colors bg-black/40 p-2 rounded-full z-10'
      >
        <XIcon className='w-4 h-4' />
      </button>

      {/* Left arrow */}
      {isMultiple && (
        <button
          onClick={() => carouselRef.current?.prev()}
          aria-label='Previous slide'
          className='absolute left-3 top-1/2 -translate-y-1/2 text-white hover:text-white transition-colors bg-black/40 hover:bg-black/60 p-2 rounded-full z-10'
        >
          <ChevronLeftIcon className='w-5 h-5' />
        </button>
      )}

      {/* Right arrow */}
      {isMultiple && (
        <button
          onClick={() => carouselRef.current?.next()}
          aria-label='Next slide'
          className='absolute right-3 top-1/2 -translate-y-1/2 text-white hover:text-white transition-colors bg-black/40 hover:bg-black/60 p-2 rounded-full z-10'
        >
          <ChevronRightIcon className='w-5 h-5' />
        </button>
      )}

      <Carousel
        ref={carouselRef}
        autoplay={isMultiple}
        autoplaySpeed={3000}
        dots={isMultiple}
        infinite={isMultiple}
        className='route-popup-carousel'
      >
        {images.map((image, index) => {
          const src = getImageKeyForScreen(image, screens);
          if (!src) return null;
          return (
            <div key={`popup-slide-${index}`}>
              <img
                src={src}
                alt={`Promotional popup ${index + 1}`}
                className='block w-full object-cover cursor-pointer h-[455px] sm:h-[420px] lg:h-[488px]'
                onClick={() => handleImageClick(image)}
              />
            </div>
          );
        })}
      </Carousel>
    </div>
  );
};

const UpgradeToPremiumModal = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [popupData, setPopupData] = useState<IRoutePopup | null>(null);
  const screens = useBreakpoint();
  const router = useRouter();
  const userData: User | undefined = useRecoilValue(user);

  const handleCancel = useCallback(() => {
    if (popupData?._id && typeof window !== 'undefined') {
      const expireHours = popupData.expireIn || 24;
      const expiryTime = Date.now() + expireHours * 60 * 60 * 1000;
      localStorage.setItem(
        `${POPUP_HIDE_KEY_PREFIX}${popupData._id}`,
        String(expiryTime),
      );
    }
    setIsModalVisible(false);
  }, [popupData]);

  useEffect(() => {
    const fetchRoutePopup = async () => {
      try {
        const response = await getRoutePopup(router.pathname);
        if (
          response.data &&
          response.data.isActive &&
          response.data.route.includes(router.pathname)
        ) {
          if (!isRoutePopupHidden(response.data._id)) {
            setPopupData(response.data);
            setIsModalVisible(true);
          } else {
            setPopupData(null);
            setIsModalVisible(false);
          }
        } else {
          setPopupData(null);
          setIsModalVisible(false);
        }
      } catch {
        setPopupData(null);
        setIsModalVisible(false);
      }
    };
    fetchRoutePopup();
  }, [router.pathname]);

  useEffect(() => {


    if (isModalVisible && userData && popupData) {

      const eventParams: EventDetails = {
        event_name: 'popup_view',
        source: 'global',
        source_type: 'route_popup',
        source_id: popupData._id,
        unit: 'boolean',
        value: 1,
      };

      logEvent({
        eventDetails: eventParams,
        userData: userData,
        pathname: router.pathname,
      });
    }
  }, [isModalVisible, userData]);

  if (!popupData) return null;

  return (
    <AntModal
      open={isModalVisible}
      onCancel={handleCancel}
      footer={null}
      centered
      closable={screens.lg ? false : true}
      closeIcon={
        <button
          aria-label='Close modal'
          className=' text-white hover:text-white transition-colors bg-white/10 p-1.5 rounded-full z-10'
        >
          <XIcon className='w-3 h-3' />
        </button>
      }
      width='auto'
      maskClosable={true}
      className='m-0 premium-modal'
      classNames={{
        content: 'p-0 w-full md:rounded-xl',
      }}
      destroyOnHidden
      wrapClassName=' p-0 premium-modal'
    >
      <RoutePopupModalContent onClose={handleCancel} popupData={popupData} />
    </AntModal>
  );
};

export default UpgradeToPremiumModal;
